/* oxlint-disable jsx-a11y/no-noninteractive-tabindex -- focused overflow regions let keyboard users pan wide schedule tables */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CallsignSchedule, CallsignScheduleCallsignsItem } from '@transreport/pauk-rrms-api'
import {
  deleteFutureBaselineCallsignSchedule,
  getBaselineCallsignSchedule,
  getEffectiveCallsignSchedulePeriods,
  getScheduledCallsignSchedules,
  removeCallsignScheduleOverride,
  setBaselineCallsignSchedule,
  setCallsignScheduleOverride,
} from '@transreport/pauk-rrms-api'
import { addDays, format, isBefore, isValid, parseISO, startOfDay } from 'date-fns'
import { Pencil, Trash2 } from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  AppButton,
  AppSwitchToggle,
  Modal,
  SkeletonBlock,
  StaffAutocomplete,
  UnsavedChangesBlocker,
} from '@/components/framework'
import { useOperatingStations } from '@/hooks/useOperatingStations'
import { trackOutcome } from '@/lib/analytics'
import { useStationStore } from '@/lib/stores/station'
import { sortShiftRows } from '@/lib/timeline/shiftOrdering'
import { isOvernightTimeRange, parseTimeToMinutes } from '@/lib/timeline/time'

type CallsignScheduleRow = {
  id: string
  callsign: string
  startHour: string
  startMinute: string
  endHour: string
  endMinute: string
  staff: string
  userId?: string | null
}

type ScheduleType = 'baseline' | 'override'
type BaselineApplicability = 'weekdays' | 'weekends'
type ScheduleApplicability = 'all_days' | BaselineApplicability
type ScheduleSortKey = 'callsign' | 'start' | 'end' | 'staff'
type ScheduleSortDirection = 'asc' | 'desc'
type ScheduleSort = { key: ScheduleSortKey; direction: ScheduleSortDirection }

type ResourcesSchedulePageProps = {
  scheduleType?: ScheduleType
  initialStartDate?: string
  initialEndDate?: string
}

const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'))
const EMPTY_CALLSIGNS: CallsignScheduleCallsignsItem[] = []

function isMissingResponse(error: unknown): boolean {
  return error instanceof Response && error.status === 404
}

function getFirstDateForApplicability(startDate: Date, applicability: BaselineApplicability): Date {
  for (let offset = 0; offset < 7; offset += 1) {
    const candidate = addDays(startDate, offset)
    const day = candidate.getDay()
    const isWeekend = day === 0 || day === 6

    if (
      (applicability === 'weekends' && isWeekend) ||
      (applicability === 'weekdays' && !isWeekend)
    ) {
      return candidate
    }
  }

  return startDate
}

export function getNextNumericalCallsign(rows: CallsignScheduleRow[]): string {
  const usedCallsigns = new Set(rows.map((row) => row.callsign.trim()))

  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const match = /^(.*\D)(\d+)$/.exec(rows[index].callsign.trim())
    if (!match) continue

    const [, prefix, digits] = match
    let nextNumber = Number(digits) + 1
    let nextCallsign = `${prefix}${nextNumber.toString().padStart(digits.length, '0')}`
    while (usedCallsigns.has(nextCallsign)) {
      nextNumber += 1
      nextCallsign = `${prefix}${nextNumber.toString().padStart(digits.length, '0')}`
    }

    return nextCallsign
  }

  return ''
}

function isZeroHourShift(row: CallsignScheduleRow): boolean {
  return row.startHour === row.endHour && row.startMinute === row.endMinute
}

function parseTimeToHourMinute(time: string): { hour: string; minute: string } {
  const [h, m] = time.split(':')
  const hour = h != null && h.length === 2 ? h : '08'
  const minute = m != null && m.length === 2 ? m : '00'
  return { hour, minute }
}

function callsignsToScheduleRows(
  callsigns: CallsignScheduleCallsignsItem[]
): CallsignScheduleRow[] {
  return callsigns.map((c) => {
    const start = parseTimeToHourMinute(c.start_time)
    const end = parseTimeToHourMinute(c.end_time)
    return {
      id: c.id,
      callsign: c.name,
      startHour: start.hour,
      startMinute: start.minute,
      endHour: end.hour,
      endMinute: end.minute,
      staff: c.user_name ?? '',
      userId: c.user_id ?? null,
    }
  })
}

function sortScheduleRows(rows: CallsignScheduleRow[]): CallsignScheduleRow[] {
  return sortShiftRows(rows, (row) => ({
    id: row.id,
    name: row.callsign,
    startTime: `${row.startHour}:${row.startMinute}`,
    endTime: `${row.endHour}:${row.endMinute}`,
  }))
}

function compareScheduleRows(
  a: CallsignScheduleRow,
  b: CallsignScheduleRow,
  sort: ScheduleSort
): number {
  const value = (row: CallsignScheduleRow): string | number => {
    if (sort.key === 'callsign') return row.callsign.trim().toLocaleLowerCase()
    if (sort.key === 'staff') return row.staff.trim().toLocaleLowerCase()
    const time =
      sort.key === 'start'
        ? `${row.startHour}:${row.startMinute}`
        : `${row.endHour}:${row.endMinute}`
    return parseTimeToMinutes(time) ?? Number.MAX_SAFE_INTEGER
  }

  const first = value(a)
  const second = value(b)
  const primary =
    typeof first === 'number' && typeof second === 'number'
      ? first - second
      : String(first).localeCompare(String(second))
  if (primary !== 0) return sort.direction === 'asc' ? primary : -primary

  return sortScheduleRows([a, b]).indexOf(a) - sortScheduleRows([a, b]).indexOf(b)
}

function sortScheduleRowsForDisplay(
  rows: CallsignScheduleRow[],
  sort: ScheduleSort
): CallsignScheduleRow[] {
  return rows.toSorted((a, b) => compareScheduleRows(a, b, sort))
}

export const ResourcesSchedulePage: React.FC<ResourcesSchedulePageProps> = ({
  scheduleType = 'baseline',
  initialStartDate,
  initialEndDate,
}) => {
  const { t } = useTranslation()
  const minSelectableDate = useMemo(() => startOfDay(new Date()), [])
  const defaultDate = useMemo(() => {
    const requestedDate = initialStartDate ? startOfDay(parseISO(initialStartDate)) : null
    return requestedDate && isValid(requestedDate) && !isBefore(requestedDate, minSelectableDate)
      ? requestedDate
      : minSelectableDate
  }, [initialStartDate, minSelectableDate])
  const [selectedDate, setSelectedDate] = useState<Date>(defaultDate)
  const [overrideEndDate, setOverrideEndDate] = useState<Date>(() => {
    const requestedDate = initialEndDate ? startOfDay(parseISO(initialEndDate)) : null
    return requestedDate && isValid(requestedDate) && !isBefore(requestedDate, defaultDate)
      ? requestedDate
      : defaultDate
  })
  const [dateInputValue, setDateInputValue] = useState<string>(() =>
    format(defaultDate, 'yyyy-MM-dd')
  )
  const [overrideEndInputValue, setOverrideEndInputValue] = useState<string>(() =>
    format(overrideEndDate, 'yyyy-MM-dd')
  )
  const [draftRows, setDraftRows] = useState<
    Partial<Record<ScheduleApplicability, CallsignScheduleRow[]>>
  >({})
  const [splitModeOverride, setSplitModeOverride] = useState<boolean | null>(null)
  const [activeScheduleApplicability, setActiveScheduleApplicability] =
    useState<BaselineApplicability>('weekdays')
  const [scheduleSort, setScheduleSort] = useState<ScheduleSort>({
    key: 'start',
    direction: 'asc',
  })
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false)
  const [schedulePendingDeletion, setSchedulePendingDeletion] = useState<CallsignSchedule | null>(
    null
  )
  const scheduleEditorRef = useRef<HTMLElement>(null)
  const { selectedStationCode } = useOperatingStations()
  const setStationSelectionLocked = useStationStore((state) => state.setStationSelectionLocked)

  const queryClient = useQueryClient()

  const dateParam = useMemo(() => format(selectedDate, 'yyyy-MM-dd'), [selectedDate])
  const overrideEndParam = useMemo(() => format(overrideEndDate, 'yyyy-MM-dd'), [overrideEndDate])
  const overrideActiveUntilParam = useMemo(
    () => format(addDays(overrideEndDate, 1), 'yyyy-MM-dd'),
    [overrideEndDate]
  )
  const isOverridePeriodValid = !isBefore(overrideEndDate, selectedDate)
  const weekdayDateParam = useMemo(
    () => format(getFirstDateForApplicability(selectedDate, 'weekdays'), 'yyyy-MM-dd'),
    [selectedDate]
  )
  const weekendDateParam = useMemo(
    () => format(getFirstDateForApplicability(selectedDate, 'weekends'), 'yyyy-MM-dd'),
    [selectedDate]
  )

  useEffect(() => {
    if (isBefore(overrideEndDate, selectedDate)) {
      setOverrideEndDate(selectedDate)
    }
  }, [overrideEndDate, selectedDate])

  useEffect(() => {
    setDraftRows({})
    setSplitModeOverride(null)
    setActiveScheduleApplicability('weekdays')
  }, [dateParam, overrideActiveUntilParam, scheduleType, selectedStationCode])

  const minDate = useMemo(() => format(minSelectableDate, 'yyyy-MM-dd'), [minSelectableDate])

  useEffect(() => {
    setDateInputValue(dateParam)
  }, [dateParam])

  useEffect(() => {
    setOverrideEndInputValue(overrideEndParam)
  }, [overrideEndParam])

  const baselineWeekdayQuery = useQuery({
    queryKey: ['callsignSchedule', 'baseline', 'weekdays', weekdayDateParam, selectedStationCode],
    queryFn: () =>
      getBaselineCallsignSchedule({
        on: weekdayDateParam,
        station_code: selectedStationCode ?? '',
      }),
    enabled: scheduleType === 'baseline' && Boolean(selectedStationCode),
  })

  const baselineWeekendQuery = useQuery({
    queryKey: ['callsignSchedule', 'baseline', 'weekends', weekendDateParam, selectedStationCode],
    queryFn: () =>
      getBaselineCallsignSchedule({
        on: weekendDateParam,
        station_code: selectedStationCode ?? '',
      }),
    enabled: scheduleType === 'baseline' && Boolean(selectedStationCode),
  })

  const effectiveScheduleQuery = useQuery({
    queryKey: [
      'callsignSchedule',
      'effective',
      dateParam,
      overrideActiveUntilParam,
      selectedStationCode,
    ],
    queryFn: () =>
      getEffectiveCallsignSchedulePeriods({
        active_from: dateParam,
        active_until: overrideActiveUntilParam,
        station_code: selectedStationCode ?? '',
      }),
    enabled: scheduleType === 'override' && Boolean(selectedStationCode) && isOverridePeriodValid,
  })

  const scheduledSchedulesQuery = useQuery({
    queryKey: ['callsignSchedule', 'scheduled', minDate, selectedStationCode],
    queryFn: () =>
      getScheduledCallsignSchedules({
        on: minDate,
        station_code: selectedStationCode ?? '',
      }),
    enabled: Boolean(selectedStationCode),
  })

  const visibleScheduledSchedules = useMemo(
    () =>
      (scheduledSchedulesQuery.data ?? []).filter((scheduledSchedule) =>
        scheduleType === 'baseline'
          ? scheduledSchedule.schedule_type === 'baseline' &&
            scheduledSchedule.active_from > minDate
          : scheduledSchedule.schedule_type === 'override' &&
            Boolean(scheduledSchedule.active_until)
      ),
    [minDate, scheduleType, scheduledSchedulesQuery.data]
  )

  const baselineSchedules = useMemo(
    () =>
      [baselineWeekdayQuery.data, baselineWeekendQuery.data].filter(Boolean) as CallsignSchedule[],
    [baselineWeekdayQuery.data, baselineWeekendQuery.data]
  )
  const hasSplitBaseline = baselineSchedules.some(
    (baselineSchedule) =>
      baselineSchedule.applicability === 'weekdays' || baselineSchedule.applicability === 'weekends'
  )
  const effectivePeriods = effectiveScheduleQuery.data ?? []
  const getPeriodApplicability = (period: (typeof effectivePeriods)[number]) =>
    period.applicability ?? period.schedule.applicability ?? 'all_days'
  const hasSplitEffectiveSchedule = effectivePeriods.some(
    (period) =>
      getPeriodApplicability(period) === 'weekdays' || getPeriodApplicability(period) === 'weekends'
  )
  const hasSplitSchedule =
    scheduleType === 'baseline' ? hasSplitBaseline : hasSplitEffectiveSchedule
  const isSplitSchedule = splitModeOverride ?? hasSplitSchedule
  const activeApplicability: ScheduleApplicability = isSplitSchedule
    ? activeScheduleApplicability
    : 'all_days'
  const selectedDayBaselineSchedule =
    weekdayDateParam === dateParam ? baselineWeekdayQuery.data : baselineWeekendQuery.data
  const allDaysSchedule =
    selectedDayBaselineSchedule &&
    (!selectedDayBaselineSchedule.applicability ||
      selectedDayBaselineSchedule.applicability === 'all_days')
      ? selectedDayBaselineSchedule
      : baselineSchedules.find(
          (baselineSchedule) =>
            !baselineSchedule.applicability || baselineSchedule.applicability === 'all_days'
        )
  const weekdaySchedule =
    baselineSchedules.find((baselineSchedule) => baselineSchedule.applicability === 'weekdays') ??
    allDaysSchedule
  const weekendSchedule =
    baselineSchedules.find((baselineSchedule) => baselineSchedule.applicability === 'weekends') ??
    allDaysSchedule
  const allDaysEffectiveSchedule = effectivePeriods.find(
    (period) => getPeriodApplicability(period) === 'all_days'
  )?.schedule
  const weekdayEffectiveSchedule =
    effectivePeriods.find((period) => getPeriodApplicability(period) === 'weekdays')?.schedule ??
    allDaysEffectiveSchedule ??
    effectivePeriods[0]?.schedule
  const weekendEffectiveSchedule =
    effectivePeriods.find((period) => getPeriodApplicability(period) === 'weekends')?.schedule ??
    allDaysEffectiveSchedule ??
    effectivePeriods[0]?.schedule
  const isLoading =
    scheduleType === 'baseline'
      ? baselineWeekdayQuery.isLoading || baselineWeekendQuery.isLoading
      : effectiveScheduleQuery.isLoading
  const baselineQueryError = [baselineWeekdayQuery.error, baselineWeekendQuery.error].find(
    (error) => error && !isMissingResponse(error)
  )
  const queryError = scheduleType === 'baseline' ? baselineQueryError : effectiveScheduleQuery.error
  const isMissingSchedule =
    scheduleType === 'override' && isMissingResponse(effectiveScheduleQuery.error)
  const isMissingOverrideBaseline = scheduleType === 'override' && isMissingSchedule
  const spansMultipleEffectiveSchedules =
    scheduleType === 'override' &&
    (['all_days', 'weekdays', 'weekends'] as const).some(
      (applicability) =>
        effectivePeriods.filter((period) => getPeriodApplicability(period) === applicability)
          .length > 1
    )
  const loadError = isMissingSchedule ? null : queryError
  const sourceRows: Record<ScheduleApplicability, CallsignScheduleRow[]> = {
    all_days: callsignsToScheduleRows(
      (scheduleType === 'baseline'
        ? allDaysSchedule?.callsigns
        : (allDaysEffectiveSchedule ?? effectivePeriods[0]?.schedule)?.callsigns) ?? EMPTY_CALLSIGNS
    ),
    weekdays: callsignsToScheduleRows(
      (scheduleType === 'baseline' ? weekdaySchedule : weekdayEffectiveSchedule)?.callsigns ??
        EMPTY_CALLSIGNS
    ),
    weekends: callsignsToScheduleRows(
      (scheduleType === 'baseline' ? weekendSchedule : weekendEffectiveSchedule)?.callsigns ??
        EMPTY_CALLSIGNS
    ),
  }
  const unsortedRows = draftRows[activeApplicability] ?? sourceRows[activeApplicability]
  const rows = useMemo(
    () => sortScheduleRowsForDisplay(unsortedRows, scheduleSort),
    [scheduleSort, unsortedRows]
  )
  const hasChanges =
    Object.keys(draftRows).length > 0 ||
    (splitModeOverride !== null && splitModeOverride !== hasSplitSchedule)

  const handleDateChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value
    setDateInputValue(value)
    if (!value) return

    const next = parseISO(value)
    const normalizedNext = startOfDay(next)

    if (isValid(next) && !isBefore(normalizedNext, minSelectableDate)) {
      setSelectedDate(normalizedNext)
    }
  }

  const handleDateBlur: React.FocusEventHandler<HTMLInputElement> = () => {
    const next = parseISO(dateInputValue)
    const normalizedNext = startOfDay(next)

    if (!isValid(next) || isBefore(normalizedNext, minSelectableDate)) {
      setDateInputValue(minDate)
      setSelectedDate(minSelectableDate)
      return
    }

    const normalizedDate = format(normalizedNext, 'yyyy-MM-dd')
    setDateInputValue(normalizedDate)
    setSelectedDate(normalizedNext)
  }

  const handleOverrideEndChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value
    setOverrideEndInputValue(value)
    if (!value) return

    const next = parseISO(value)
    const normalizedNext = startOfDay(next)

    if (isValid(next) && !isBefore(normalizedNext, selectedDate)) {
      setOverrideEndDate(normalizedNext)
    }
  }

  const handleOverrideEndBlur: React.FocusEventHandler<HTMLInputElement> = () => {
    const next = parseISO(overrideEndInputValue)
    const normalizedNext = startOfDay(next)

    if (!isValid(next) || isBefore(normalizedNext, selectedDate)) {
      setOverrideEndInputValue(dateParam)
      setOverrideEndDate(selectedDate)
      return
    }

    const normalizedDate = format(normalizedNext, 'yyyy-MM-dd')
    setOverrideEndInputValue(normalizedDate)
    setOverrideEndDate(normalizedNext)
  }

  const handleRowChange = <K extends keyof CallsignScheduleRow>(
    rowId: string,
    key: K,
    value: CallsignScheduleRow[K]
  ) => {
    setDraftRows((current) => ({
      ...current,
      [activeApplicability]: (current[activeApplicability] ?? unsortedRows).map((row) =>
        row.id === rowId ? { ...row, [key]: value } : row
      ),
    }))
  }

  const handleDeleteRow = (rowId: string) => {
    setDraftRows((current) => ({
      ...current,
      [activeApplicability]: (current[activeApplicability] ?? unsortedRows).filter(
        (row) => row.id !== rowId
      ),
    }))
  }

  const handleSaveChanges = () => {
    if (saveValidationError) {
      toast.error(saveValidationError)
      return
    }

    setIsSaveModalOpen(true)
  }

  const handleRevertChanges = () => {
    setIsRevertModalOpen(true)
  }

  const handleAddRow = () => {
    const newRow: CallsignScheduleRow = {
      id: `local-${Date.now()}-${rows.length + 1}`,
      callsign: getNextNumericalCallsign(rows),
      startHour: '08',
      startMinute: '00',
      endHour: '16',
      endMinute: '00',
      staff: '',
    }

    setDraftRows((current) => ({
      ...current,
      [activeApplicability]: [...(current[activeApplicability] ?? unsortedRows), newRow],
    }))
  }

  const handleSplitScheduleChange = (isSplit: boolean) => {
    setSplitModeOverride(isSplit)

    if (isSplit) {
      const sevenDayRows = draftRows.all_days ?? sourceRows.all_days
      setDraftRows((current) => ({
        ...current,
        weekdays: current.weekdays ?? sevenDayRows.map((row) => ({ ...row })),
        weekends: current.weekends ?? sevenDayRows.map((row) => ({ ...row })),
      }))
      setActiveScheduleApplicability('weekdays')
      return
    }

    const activeRows =
      draftRows[activeScheduleApplicability] ?? sourceRows[activeScheduleApplicability]
    setDraftRows((current) => ({
      ...current,
      all_days: activeRows.map((row) => ({ ...row })),
    }))
  }

  const hasInvalidDateInput = useMemo(() => {
    const parsedInputDate = parseISO(dateInputValue)
    return !isValid(parsedInputDate) || isBefore(startOfDay(parsedInputDate), minSelectableDate)
  }, [dateInputValue, minSelectableDate])

  const hasInvalidOverrideEndInput = useMemo(() => {
    if (scheduleType !== 'override') return false

    const parsedEndDate = parseISO(overrideEndInputValue)
    return !isValid(parsedEndDate) || isBefore(startOfDay(parsedEndDate), selectedDate)
  }, [overrideEndInputValue, scheduleType, selectedDate])

  const weekdayRows = draftRows.weekdays ?? sourceRows.weekdays
  const weekendRows = draftRows.weekends ?? sourceRows.weekends
  const allDaysRows = draftRows.all_days ?? sourceRows.all_days
  const savedWeekdayRows = sortScheduleRows(weekdayRows)
  const savedWeekendRows = sortScheduleRows(weekendRows)
  const savedAllDaysRows = sortScheduleRows(allDaysRows)
  const savedRows = sortScheduleRows(rows)
  const schedulesRowsToSave = isSplitSchedule
    ? [savedWeekdayRows, savedWeekendRows]
    : [scheduleType === 'baseline' ? savedAllDaysRows : savedRows]
  const hasNoRows = schedulesRowsToSave.some((scheduleRows) => scheduleRows.length === 0)
  const hasBlankCallsigns = schedulesRowsToSave.some((scheduleRows) =>
    scheduleRows.some((row) => row.callsign.trim().length === 0)
  )
  const hasZeroHourShifts = schedulesRowsToSave.some((scheduleRows) =>
    scheduleRows.some(isZeroHourShift)
  )

  const pageValidationError = useMemo(() => {
    if (!selectedStationCode) {
      return t('resources.schedule.validation.stationRequired')
    }

    if (hasInvalidDateInput) {
      return t('resources.schedule.validation.effectiveFromMin')
    }

    if (hasInvalidOverrideEndInput) {
      return t('resources.schedule.validation.overrideEndBeforeStart')
    }

    if (hasNoRows) {
      return t('resources.schedule.validation.noRows')
    }

    return null
  }, [hasInvalidDateInput, hasInvalidOverrideEndInput, hasNoRows, selectedStationCode, t])
  const saveValidationError =
    pageValidationError ??
    (hasBlankCallsigns
      ? t('resources.schedule.validation.callsignRequired')
      : hasZeroHourShifts
        ? t('resources.schedule.validation.zeroHourShift')
        : null)

  const callsignPayload = (scheduleRows: CallsignScheduleRow[]) =>
    scheduleRows.map((row, position) => ({
      name: row.callsign.trim(),
      start_time: `${row.startHour}:${row.startMinute}`,
      end_time: `${row.endHour}:${row.endMinute}`,
      user_id: row.userId ?? null,
      position,
    }))

  const getSelectableStartDate = (scheduledSchedule: CallsignSchedule) =>
    scheduledSchedule.active_from < minDate ? minDate : scheduledSchedule.active_from

  const saveScheduleMutation = useMutation({
    mutationKey: ['callsignSchedule', 'save'],
    meta: {
      analytics: {
        event: 'schedule_saved',
        area: 'schedule',
        parameters: () => ({ schedule_type: scheduleType }),
      },
      toast: {
        success: t('resources.schedule.saveSuccess'),
        error: t('resources.saveChangesModal.error'),
      },
    },
    mutationFn: async () => {
      if (!selectedStationCode) return

      if (scheduleType === 'baseline') {
        if (isSplitSchedule) {
          await Promise.all([
            setBaselineCallsignSchedule(dateParam, {
              station_code: selectedStationCode,
              applicability: 'weekdays',
              callsigns: callsignPayload(savedWeekdayRows),
            }),
            setBaselineCallsignSchedule(dateParam, {
              station_code: selectedStationCode,
              applicability: 'weekends',
              callsigns: callsignPayload(savedWeekendRows),
            }),
          ])
        } else {
          await setBaselineCallsignSchedule(dateParam, {
            station_code: selectedStationCode,
            applicability: 'all_days',
            callsigns: callsignPayload(savedAllDaysRows),
          })
        }
      } else {
        const saveOverride = (
          applicability: ScheduleApplicability,
          scheduleRows: CallsignScheduleRow[]
        ) =>
          setCallsignScheduleOverride(dateParam, {
            active_until: overrideActiveUntilParam,
            station_code: selectedStationCode,
            applicability,
            callsigns: callsignPayload(scheduleRows),
          })

        if (isSplitSchedule) {
          await Promise.all([
            saveOverride('weekdays', savedWeekdayRows),
            saveOverride('weekends', savedWeekendRows),
          ])
        } else {
          await saveOverride('all_days', savedRows)
        }
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['callsignSchedule'] })
      setDraftRows({})
      setSplitModeOverride(null)
      setIsSaveModalOpen(false)
    },
  })

  const removeScheduleMutation = useMutation({
    mutationKey: ['callsignSchedule', 'remove'],
    meta: {
      analytics: {
        event: 'schedule_removed',
        area: 'schedule',
        parameters: () => ({
          schedule_type: schedulePendingDeletion?.schedule_type ?? scheduleType,
        }),
      },
      toast: {
        success: t('resources.schedule.deleteModal.success'),
        error: t('resources.schedule.deleteModal.error'),
      },
    },
    mutationFn: async () => {
      if (!selectedStationCode || !schedulePendingDeletion) return

      if (schedulePendingDeletion.schedule_type === 'baseline') {
        await deleteFutureBaselineCallsignSchedule(schedulePendingDeletion.id)
      } else if (schedulePendingDeletion.active_until) {
        await removeCallsignScheduleOverride(getSelectableStartDate(schedulePendingDeletion), {
          active_until: schedulePendingDeletion.active_until,
          station_code: selectedStationCode,
          applicability: schedulePendingDeletion.applicability ?? 'all_days',
        })
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['callsignSchedule'] })
      setDraftRows({})
      setSchedulePendingDeletion(null)
    },
  })

  const isSaving = saveScheduleMutation.isPending
  const isRemoving = removeScheduleMutation.isPending
  const isWorking = isSaving || isRemoving
  const canSaveSchedule = hasChanges || scheduleType === 'override'

  useEffect(() => {
    setStationSelectionLocked(hasChanges || isWorking)
    return () => setStationSelectionLocked(false)
  }, [hasChanges, isWorking, setStationSelectionLocked])

  const handleCloseSaveModal = () => {
    if (isSaving) return
    setIsSaveModalOpen(false)
  }

  const handleConfirmSave = () => {
    if (saveValidationError) {
      trackOutcome('schedule_saved', 'schedule', 'attempted', {
        schedule_type: scheduleType,
      })
      trackOutcome('schedule_saved', 'schedule', 'failed', {
        failure_type: 'validation',
        schedule_type: scheduleType,
      })
      toast.error(saveValidationError)
      return
    }

    if (!selectedStationCode) {
      toast.error(t('resources.saveChangesModal.error'))
      return
    }

    saveScheduleMutation.mutate()
  }

  const handleCloseRemoveModal = () => {
    if (isRemoving) return
    setSchedulePendingDeletion(null)
  }

  const handleConfirmRemove = () => {
    if (!selectedStationCode || !schedulePendingDeletion) return
    removeScheduleMutation.mutate()
  }

  const getInclusiveEndDate = (scheduledSchedule: CallsignSchedule) =>
    scheduledSchedule.active_until
      ? format(addDays(parseISO(scheduledSchedule.active_until), -1), 'yyyy-MM-dd')
      : null

  const isScheduledScheduleSelected = (scheduledSchedule: CallsignSchedule) => {
    const startDate = getSelectableStartDate(scheduledSchedule)
    if (scheduleType === 'baseline') {
      return (
        dateParam === startDate &&
        (!isSplitSchedule || scheduledSchedule.applicability === activeScheduleApplicability)
      )
    }

    return (
      dateParam === startDate &&
      overrideEndParam === getInclusiveEndDate(scheduledSchedule) &&
      (!isSplitSchedule || scheduledSchedule.applicability === activeScheduleApplicability)
    )
  }

  const handleScheduledScheduleSelect = (scheduledSchedule: CallsignSchedule) => {
    if (hasChanges || isWorking) return

    const startDate = getSelectableStartDate(scheduledSchedule)
    setSelectedDate(startOfDay(parseISO(startDate)))

    if (
      scheduledSchedule.applicability === 'weekdays' ||
      scheduledSchedule.applicability === 'weekends'
    ) {
      setActiveScheduleApplicability(scheduledSchedule.applicability)
    }

    const inclusiveEndDate = getInclusiveEndDate(scheduledSchedule)
    if (scheduledSchedule.schedule_type === 'override' && inclusiveEndDate) {
      setOverrideEndDate(startOfDay(parseISO(inclusiveEndDate)))
    }

    requestAnimationFrame(() =>
      scheduleEditorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    )
  }

  const toggleScheduleSort = (key: ScheduleSortKey) => {
    setScheduleSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const renderSortableHeader = (key: ScheduleSortKey, label: string) => {
    const active = scheduleSort.key === key
    const direction = active ? scheduleSort.direction : undefined
    return (
      <th
        scope="col"
        aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
        className="px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase"
      >
        <button
          type="button"
          className="inline-flex items-center gap-1 text-left focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
          aria-label={t('resources.schedule.sort.sortBy', { column: label })}
          onClick={() => toggleScheduleSort(key)}
        >
          {label}
          <span aria-hidden="true">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
        </button>
      </th>
    )
  }

  return (
    <div className="flex size-full flex-col bg-gray-50">
      <h1 className="sr-only">{t('resources.schedule.title')}</h1>

      <div className="flex-1 overflow-auto px-3 py-4 sm:p-6">
        <div className="flex w-full flex-col gap-6">
          <section
            ref={scheduleEditorRef}
            aria-label={t('resources.schedule.editorAriaLabel')}
            aria-busy={isLoading || isWorking}
            className="rounded-lg bg-white p-5 shadow-sm"
          >
            <div>
              <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="resources-schedule-date"
                      className="text-sm font-medium text-gray-700"
                    >
                      {t(
                        scheduleType === 'baseline'
                          ? 'resources.schedule.effectiveFromLabel'
                          : 'resources.schedule.overrideStartLabel'
                      )}
                    </label>
                    <input
                      id="resources-schedule-date"
                      type="date"
                      min={minDate}
                      value={dateInputValue}
                      aria-invalid={hasInvalidDateInput}
                      aria-describedby={
                        hasInvalidDateInput ? 'resources-schedule-validation' : undefined
                      }
                      onChange={handleDateChange}
                      onBlur={handleDateBlur}
                      disabled={hasChanges || isWorking}
                      className="rounded border border-border-gray px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </div>
                  {scheduleType === 'override' && (
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="resources-override-end-date"
                        className="text-sm font-medium text-gray-700"
                      >
                        {t('resources.schedule.overrideEndLabel')}
                      </label>
                      <input
                        id="resources-override-end-date"
                        type="date"
                        min={dateParam}
                        value={overrideEndInputValue}
                        aria-invalid={hasInvalidOverrideEndInput}
                        aria-describedby={
                          hasInvalidOverrideEndInput ? 'resources-schedule-validation' : undefined
                        }
                        onChange={handleOverrideEndChange}
                        onBlur={handleOverrideEndBlur}
                        disabled={hasChanges || isWorking}
                        className="rounded border border-border-gray px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                      />
                    </div>
                  )}
                  <AppButton
                    variant="primary"
                    size="sm"
                    onClick={handleAddRow}
                    disabled={
                      !selectedStationCode ||
                      isLoading ||
                      Boolean(loadError) ||
                      isMissingOverrideBaseline
                    }
                  >
                    {t('resources.table.addCallsign')}
                  </AppButton>
                </div>
              </div>

              {selectedStationCode && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {t('resources.schedule.pattern.title')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-700">
                    <span className={!isSplitSchedule ? 'font-semibold text-gray-900' : ''}>
                      {t('resources.schedule.pattern.allDays')}
                    </span>
                    <AppSwitchToggle
                      value={isSplitSchedule}
                      ariaLabel={t('resources.schedule.pattern.toggleAriaLabel')}
                      disabled={
                        isLoading || isWorking || Boolean(loadError) || isMissingOverrideBaseline
                      }
                      onChange={handleSplitScheduleChange}
                    />
                    <span className={isSplitSchedule ? 'font-semibold text-gray-900' : ''}>
                      {t('resources.schedule.pattern.split')}
                    </span>
                  </div>
                </div>
              )}

              {isSplitSchedule && (
                <div
                  className="mb-4 flex gap-1 border-b border-gray-200"
                  role="tablist"
                  aria-label={t('resources.schedule.pattern.tabsAriaLabel')}
                >
                  {(['weekdays', 'weekends'] as const).map((applicability) => {
                    const isActive = activeScheduleApplicability === applicability
                    return (
                      <button
                        key={applicability}
                        id={`resources-schedule-tab-${applicability}`}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-controls="resources-schedule-tabpanel"
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => setActiveScheduleApplicability(applicability)}
                        onKeyDown={(event) => {
                          const tabs = ['weekdays', 'weekends'] as const
                          const currentIndex = tabs.indexOf(applicability)
                          let nextIndex = currentIndex
                          if (event.key === 'ArrowRight')
                            nextIndex = (currentIndex + 1) % tabs.length
                          if (event.key === 'ArrowLeft')
                            nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
                          if (event.key === 'Home') nextIndex = 0
                          if (event.key === 'End') nextIndex = tabs.length - 1
                          if (nextIndex === currentIndex) return
                          event.preventDefault()
                          const nextTab = tabs[nextIndex]
                          setActiveScheduleApplicability(nextTab)
                          document.getElementById(`resources-schedule-tab-${nextTab}`)?.focus()
                        }}
                        className={`cursor-pointer border-b-2 px-4 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
                          isActive
                            ? 'border-primary font-semibold text-primary'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {t(`resources.schedule.pattern.${applicability}`)}
                      </button>
                    )
                  })}
                </div>
              )}

              <div
                id={isSplitSchedule ? 'resources-schedule-tabpanel' : undefined}
                role={isSplitSchedule ? 'tabpanel' : undefined}
                aria-labelledby={
                  isSplitSchedule
                    ? `resources-schedule-tab-${activeScheduleApplicability}`
                    : undefined
                }
              >
                {!selectedStationCode && (
                  <p className="py-4 text-sm text-gray-600">{t('common.selectStationFirst')}</p>
                )}
                {selectedStationCode && isLoading && (
                  <p className="sr-only" role="status">
                    {t('resources.schedule.loading')}
                  </p>
                )}
                {selectedStationCode && loadError && (
                  <p className="py-4 text-sm text-red-600" role="alert">
                    {t('shiftTimeline.errorLoading')}
                  </p>
                )}
                {selectedStationCode && !isLoading && isMissingOverrideBaseline && (
                  <p className="py-4 text-sm text-red-600" role="alert">
                    {t('resources.schedule.overrideRequiresBaseline')}
                  </p>
                )}
                {selectedStationCode && !loadError && !isMissingOverrideBaseline && (
                  <>
                    {!isLoading && spansMultipleEffectiveSchedules && (
                      <p className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        {t('resources.schedule.multiplePeriodsWarning')}
                      </p>
                    )}
                    {!isLoading && rows.length === 0 && (
                      <p className="py-4 text-sm text-gray-600">{t('resources.table.empty')}</p>
                    )}
                    {(isLoading || rows.length > 0) && (
                      <div
                        className="overflow-x-auto"
                        role="region"
                        aria-label={t('resources.schedule.editorTableRegionLabel')}
                        tabIndex={0}
                      >
                        <table
                          aria-busy={isLoading}
                          className="w-full min-w-5xl table-auto divide-y divide-gray-200 text-sm"
                        >
                          <caption className="sr-only">
                            {t('resources.schedule.editorTableCaption', {
                              applicability: t(
                                `resources.schedule.applicability.${activeApplicability}`
                              ),
                            })}
                          </caption>
                          <colgroup>
                            <col className="w-40" />
                            <col className="w-48" />
                            <col className="w-48" />
                            <col className="w-72" />
                            <col className="w-28" />
                          </colgroup>
                          <thead className="bg-gray-50">
                            <tr>
                              {renderSortableHeader('callsign', t('resources.table.callsign'))}
                              {renderSortableHeader('start', t('resources.table.shiftStart'))}
                              {renderSortableHeader('end', t('resources.table.shiftEnd'))}
                              {renderSortableHeader('staff', t('resources.table.staff'))}
                              <th
                                scope="col"
                                className="px-3 py-2 text-right text-xs font-semibold tracking-wide text-gray-500 uppercase"
                              >
                                {t('resources.table.actions')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {isLoading &&
                              Array.from({ length: 3 }, (_, index) => (
                                <tr key={index} aria-hidden="true">
                                  <td className="p-3">
                                    <SkeletonBlock className="h-7 w-20" />
                                  </td>
                                  <td className="p-3">
                                    <div className="flex gap-2">
                                      <SkeletonBlock className="h-12 w-20" />
                                      <SkeletonBlock className="h-12 w-20" />
                                    </div>
                                  </td>
                                  <td className="p-3">
                                    <div className="flex gap-2">
                                      <SkeletonBlock className="h-12 w-20" />
                                      <SkeletonBlock className="h-12 w-20" />
                                    </div>
                                  </td>
                                  <td className="p-3">
                                    <SkeletonBlock className="h-9 w-full" />
                                  </td>
                                  <td className="p-3">
                                    <SkeletonBlock className="ml-auto h-8 w-20" />
                                  </td>
                                </tr>
                              ))}
                            {!isLoading &&
                              rows.map((row) => {
                                const hasCallsignError = row.callsign.trim().length === 0
                                const hasTimeError = isZeroHourShift(row)
                                const callsignErrorId = `callsign-error-${row.id}`
                                const timeErrorId = `shift-time-error-${row.id}`

                                return (
                                  <tr key={row.id}>
                                    <td className="px-3 py-2 align-middle whitespace-nowrap text-gray-900">
                                      {row.id.startsWith('local-') ? (
                                        <input
                                          type="text"
                                          value={row.callsign}
                                          onChange={(event) =>
                                            handleRowChange(row.id, 'callsign', event.target.value)
                                          }
                                          aria-label={t('resources.schedule.rowFieldAriaLabel', {
                                            field: t('resources.table.callsign'),
                                            callsign:
                                              row.callsign || t('resources.schedule.newCallsign'),
                                          })}
                                          aria-invalid={hasCallsignError || undefined}
                                          aria-describedby={
                                            hasCallsignError ? callsignErrorId : undefined
                                          }
                                          placeholder={t('resources.table.callsign')}
                                          className={`w-32 rounded border px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none ${hasCallsignError ? 'border-red-500 bg-red-50' : 'border-border-gray'}`}
                                        />
                                      ) : (
                                        <span className="inline-flex h-7 items-center rounded-full bg-gray-100 px-3 text-xs font-medium text-gray-800">
                                          {row.callsign}
                                        </span>
                                      )}
                                      {hasCallsignError && (
                                        <p
                                          id={callsignErrorId}
                                          role="alert"
                                          className="mt-1 text-xs text-red-600"
                                        >
                                          {t('resources.schedule.validation.callsignRequired')}
                                        </p>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 align-middle">
                                      <div className="flex items-end gap-2">
                                        <div>
                                          <label
                                            htmlFor={`default-start-hour-${row.id}`}
                                            className="mb-1 block text-xs font-medium text-gray-600"
                                          >
                                            {t('shiftTimeline.addBreakModal.hours')}
                                          </label>
                                          <select
                                            id={`default-start-hour-${row.id}`}
                                            value={row.startHour}
                                            aria-label={t('resources.schedule.rowFieldAriaLabel', {
                                              field: `${t('resources.table.shiftStart')} ${t('shiftTimeline.addBreakModal.hours')}`,
                                              callsign: row.callsign,
                                            })}
                                            aria-invalid={hasTimeError || undefined}
                                            aria-describedby={
                                              hasTimeError ? timeErrorId : undefined
                                            }
                                            onChange={(event) =>
                                              handleRowChange(
                                                row.id,
                                                'startHour',
                                                event.target.value
                                              )
                                            }
                                            className={`w-20 rounded border px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none ${hasTimeError ? 'border-red-500 bg-red-50' : 'border-border-gray'}`}
                                          >
                                            {HOURS.map((hour) => (
                                              <option key={hour} value={hour}>
                                                {hour}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label
                                            htmlFor={`default-start-minute-${row.id}`}
                                            className="mb-1 block text-xs font-medium text-gray-600"
                                          >
                                            {t('shiftTimeline.addBreakModal.minutes')}
                                          </label>
                                          <select
                                            id={`default-start-minute-${row.id}`}
                                            value={row.startMinute}
                                            aria-label={t('resources.schedule.rowFieldAriaLabel', {
                                              field: `${t('resources.table.shiftStart')} ${t('shiftTimeline.addBreakModal.minutes')}`,
                                              callsign: row.callsign,
                                            })}
                                            aria-invalid={hasTimeError || undefined}
                                            aria-describedby={
                                              hasTimeError ? timeErrorId : undefined
                                            }
                                            onChange={(event) =>
                                              handleRowChange(
                                                row.id,
                                                'startMinute',
                                                event.target.value
                                              )
                                            }
                                            className={`w-20 rounded border px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none ${hasTimeError ? 'border-red-500 bg-red-50' : 'border-border-gray'}`}
                                          >
                                            {MINUTES.map((minute) => (
                                              <option key={minute} value={minute}>
                                                {minute}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 align-middle">
                                      <div className="flex items-end gap-2">
                                        <div>
                                          <label
                                            htmlFor={`default-end-hour-${row.id}`}
                                            className="mb-1 block text-xs font-medium text-gray-600"
                                          >
                                            {t('shiftTimeline.addBreakModal.hours')}
                                          </label>
                                          <select
                                            id={`default-end-hour-${row.id}`}
                                            value={row.endHour}
                                            aria-label={t('resources.schedule.rowFieldAriaLabel', {
                                              field: `${t('resources.table.shiftEnd')} ${t('shiftTimeline.addBreakModal.hours')}`,
                                              callsign: row.callsign,
                                            })}
                                            aria-invalid={hasTimeError || undefined}
                                            aria-describedby={
                                              hasTimeError ? timeErrorId : undefined
                                            }
                                            onChange={(event) =>
                                              handleRowChange(row.id, 'endHour', event.target.value)
                                            }
                                            className={`w-20 rounded border px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none ${hasTimeError ? 'border-red-500 bg-red-50' : 'border-border-gray'}`}
                                          >
                                            {HOURS.map((hour) => (
                                              <option key={hour} value={hour}>
                                                {hour}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label
                                            htmlFor={`default-end-minute-${row.id}`}
                                            className="mb-1 block text-xs font-medium text-gray-600"
                                          >
                                            {t('shiftTimeline.addBreakModal.minutes')}
                                          </label>
                                          <select
                                            id={`default-end-minute-${row.id}`}
                                            value={row.endMinute}
                                            aria-label={t('resources.schedule.rowFieldAriaLabel', {
                                              field: `${t('resources.table.shiftEnd')} ${t('shiftTimeline.addBreakModal.minutes')}`,
                                              callsign: row.callsign,
                                            })}
                                            aria-invalid={hasTimeError || undefined}
                                            aria-describedby={
                                              hasTimeError ? timeErrorId : undefined
                                            }
                                            onChange={(event) =>
                                              handleRowChange(
                                                row.id,
                                                'endMinute',
                                                event.target.value
                                              )
                                            }
                                            className={`w-20 rounded border px-2 py-1 text-xs focus:ring-2 focus:ring-primary focus:outline-none ${hasTimeError ? 'border-red-500 bg-red-50' : 'border-border-gray'}`}
                                          >
                                            {MINUTES.map((minute) => (
                                              <option key={minute} value={minute}>
                                                {minute}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                      {hasTimeError ? (
                                        <p
                                          id={timeErrorId}
                                          role="alert"
                                          className="mt-1 text-xs text-red-600"
                                        >
                                          {t('resources.schedule.validation.zeroHourShift')}
                                        </p>
                                      ) : (
                                        isOvernightTimeRange(
                                          `${row.startHour}:${row.startMinute}`,
                                          `${row.endHour}:${row.endMinute}`
                                        ) && (
                                          <p className="mt-1 text-xs font-medium text-indigo-700">
                                            {t('resources.schedule.overnightHint')}
                                          </p>
                                        )
                                      )}
                                    </td>
                                    <td className="px-3 py-2 align-middle">
                                      <div className="max-w-72">
                                        <StaffAutocomplete
                                          value={row.staff}
                                          userId={row.userId}
                                          stationCode={selectedStationCode}
                                          placeholder={t('resources.table.staffPlaceholder')}
                                          ariaLabel={t('resources.schedule.rowFieldAriaLabel', {
                                            field: t('resources.table.staff'),
                                            callsign: row.callsign,
                                          })}
                                          onChange={(userId, displayName) => {
                                            setDraftRows((current) => ({
                                              ...current,
                                              [activeApplicability]: (
                                                current[activeApplicability] ?? rows
                                              ).map((currentRow) =>
                                                currentRow.id === row.id
                                                  ? { ...currentRow, staff: displayName, userId }
                                                  : currentRow
                                              ),
                                            }))
                                          }}
                                          onClear={() => {
                                            setDraftRows((current) => ({
                                              ...current,
                                              [activeApplicability]: (
                                                current[activeApplicability] ?? rows
                                              ).map((currentRow) =>
                                                currentRow.id === row.id
                                                  ? { ...currentRow, staff: '', userId: null }
                                                  : currentRow
                                              ),
                                            }))
                                          }}
                                        />
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 text-right align-middle">
                                      <AppButton
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleDeleteRow(row.id)}
                                      >
                                        {t('resources.table.delete')}
                                      </AppButton>
                                    </td>
                                  </tr>
                                )
                              })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {selectedStationCode && !isLoading && !loadError && (
                      <>
                        {pageValidationError && (
                          <p
                            id="resources-schedule-validation"
                            className="mt-4 text-xs text-danger"
                            role="alert"
                          >
                            {pageValidationError}
                          </p>
                        )}
                        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                          <AppButton
                            variant="secondary"
                            size="sm"
                            onClick={handleRevertChanges}
                            disabled={!hasChanges || isWorking}
                          >
                            {t('resources.actions.revert')}
                          </AppButton>
                          <AppButton
                            variant="primary"
                            size="sm"
                            onClick={handleSaveChanges}
                            disabled={!canSaveSchedule || isWorking || Boolean(saveValidationError)}
                          >
                            {t('resources.actions.saveChanges')}
                          </AppButton>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>

          {selectedStationCode &&
            (scheduledSchedulesQuery.isLoading ||
              scheduledSchedulesQuery.error ||
              visibleScheduledSchedules.length > 0) && (
              <section
                aria-labelledby="scheduled-schedules-heading"
                className="rounded-lg bg-white p-5 shadow-sm"
              >
                <h2
                  id="scheduled-schedules-heading"
                  className="text-base leading-6 font-semibold text-gray-900"
                >
                  {t(
                    scheduleType === 'baseline'
                      ? 'resources.schedule.scheduled.baselineTitle'
                      : 'resources.schedule.scheduled.overrideTitle'
                  )}
                </h2>
                {scheduledSchedulesQuery.isLoading && (
                  <p className="sr-only" role="status">
                    {t('resources.schedule.scheduled.loading')}
                  </p>
                )}
                {scheduledSchedulesQuery.error && (
                  <p className="pt-4 text-sm text-red-600" role="alert">
                    {t('resources.schedule.scheduled.loadError')}
                  </p>
                )}
                {!scheduledSchedulesQuery.error && (
                  <div
                    className="mt-4 overflow-x-auto"
                    role="region"
                    aria-label={t('resources.schedule.scheduled.tableRegionLabel')}
                    tabIndex={0}
                  >
                    <table
                      aria-busy={scheduledSchedulesQuery.isLoading}
                      className="min-w-full divide-y divide-gray-200 text-sm"
                    >
                      <caption className="sr-only">
                        {t('resources.schedule.scheduled.tableRegionLabel')}
                      </caption>
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase"
                          >
                            {t(
                              scheduleType === 'baseline'
                                ? 'resources.schedule.scheduled.effectiveFrom'
                                : 'resources.schedule.scheduled.from'
                            )}
                          </th>
                          {scheduleType === 'override' && (
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase"
                            >
                              {t('resources.schedule.scheduled.to')}
                            </th>
                          )}
                          <th
                            scope="col"
                            className="px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase"
                          >
                            {t('resources.schedule.scheduled.operatingDays')}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-xs font-semibold tracking-wide text-gray-500 uppercase"
                          >
                            {t('resources.table.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {scheduledSchedulesQuery.isLoading &&
                          Array.from({ length: 3 }, (_, index) => (
                            <tr key={index} aria-hidden="true">
                              <td className="p-3">
                                <SkeletonBlock className="h-5 w-28" />
                              </td>
                              {scheduleType === 'override' && (
                                <td className="p-3">
                                  <SkeletonBlock className="h-5 w-28" />
                                </td>
                              )}
                              <td className="p-3">
                                <SkeletonBlock className="h-5 w-24" />
                              </td>
                              <td className="p-3">
                                <div className="flex justify-end gap-2">
                                  <SkeletonBlock className="h-8 w-16" />
                                  <SkeletonBlock className="h-8 w-20" />
                                </div>
                              </td>
                            </tr>
                          ))}
                        {!scheduledSchedulesQuery.isLoading &&
                          visibleScheduledSchedules.map((scheduledSchedule) => {
                            const selectableStartDate = getSelectableStartDate(scheduledSchedule)
                            const inclusiveEndDate = getInclusiveEndDate(scheduledSchedule)
                            const isSelected = isScheduledScheduleSelected(scheduledSchedule)

                            return (
                              <tr
                                key={scheduledSchedule.id}
                                className={isSelected ? 'bg-brand-surface' : ''}
                              >
                                <td className="p-3 text-gray-900">
                                  {format(parseISO(selectableStartDate), 'd MMM yyyy')}
                                </td>
                                {scheduleType === 'override' && (
                                  <td className="p-3 text-gray-900">
                                    {inclusiveEndDate
                                      ? format(parseISO(inclusiveEndDate), 'd MMM yyyy')
                                      : '—'}
                                  </td>
                                )}
                                <td className="p-3 text-gray-900">
                                  {t(
                                    `resources.schedule.applicability.${
                                      scheduledSchedule.applicability ?? 'all_days'
                                    }`
                                  )}
                                </td>
                                <td className="p-3 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <AppButton
                                      variant="outline"
                                      size="sm"
                                      disabled={hasChanges || isWorking}
                                      title={
                                        hasChanges
                                          ? t('resources.schedule.scheduled.jumpBlocked')
                                          : undefined
                                      }
                                      buttonClass="inline-flex items-center gap-1.5"
                                      onClick={() =>
                                        handleScheduledScheduleSelect(scheduledSchedule)
                                      }
                                    >
                                      <Pencil className="size-3.5" aria-hidden="true" />
                                      {t('resources.schedule.scheduled.edit')}
                                    </AppButton>
                                    <AppButton
                                      variant="danger"
                                      size="sm"
                                      disabled={hasChanges || isWorking}
                                      title={
                                        hasChanges
                                          ? t('resources.schedule.scheduled.jumpBlocked')
                                          : undefined
                                      }
                                      buttonClass="inline-flex items-center gap-1.5"
                                      onClick={() => setSchedulePendingDeletion(scheduledSchedule)}
                                    >
                                      <Trash2 className="size-3.5" aria-hidden="true" />
                                      {t('resources.schedule.scheduled.delete')}
                                    </AppButton>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
        </div>
      </div>

      <UnsavedChangesBlocker isDirty={hasChanges} message={t('resources.revertModal.body')} />

      <Modal
        isOpen={isSaveModalOpen}
        onClose={handleCloseSaveModal}
        title={t('resources.saveChangesModal.title')}
        size="sm"
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-700">
            {t(
              scheduleType === 'baseline'
                ? 'resources.schedule.saveConfirmBody'
                : 'resources.schedule.overrideSaveConfirmBody'
            )}
          </p>
          <div className="flex justify-end gap-3">
            <AppButton
              variant="secondary"
              size="sm"
              onClick={handleCloseSaveModal}
              disabled={isSaving}
            >
              {t('resources.saveChangesModal.cancel')}
            </AppButton>
            <AppButton variant="primary" size="sm" onClick={handleConfirmSave} disabled={isSaving}>
              {isSaving ? t('common.loading') : t('resources.saveChangesModal.confirm')}
            </AppButton>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isRevertModalOpen}
        onClose={() => setIsRevertModalOpen(false)}
        title={t('resources.revertModal.title')}
        size="sm"
      >
        <div className="space-y-6">
          <p className="text-sm whitespace-pre-line text-gray-700">
            {t('resources.revertModal.body')}
          </p>
          <div className="flex justify-end gap-3">
            <AppButton variant="secondary" size="sm" onClick={() => setIsRevertModalOpen(false)}>
              {t('resources.revertModal.cancel')}
            </AppButton>
            <AppButton
              variant="primary"
              size="sm"
              onClick={() => {
                setDraftRows({})
                setSplitModeOverride(null)
                setIsRevertModalOpen(false)
              }}
            >
              {t('resources.revertModal.confirm')}
            </AppButton>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(schedulePendingDeletion)}
        onClose={handleCloseRemoveModal}
        title={t('resources.schedule.deleteModal.title')}
        size="sm"
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-700">
            {t(
              schedulePendingDeletion?.schedule_type === 'baseline'
                ? 'resources.schedule.deleteModal.baselineBody'
                : 'resources.schedule.deleteModal.overrideBody'
            )}
          </p>
          <div className="flex justify-end gap-3">
            <AppButton
              variant="secondary"
              size="sm"
              onClick={handleCloseRemoveModal}
              disabled={isRemoving}
            >
              {t('resources.schedule.deleteModal.cancel')}
            </AppButton>
            <AppButton
              variant="danger"
              size="sm"
              onClick={handleConfirmRemove}
              disabled={isRemoving}
            >
              {isRemoving ? t('common.loading') : t('resources.schedule.deleteModal.confirm')}
            </AppButton>
          </div>
        </div>
      </Modal>
    </div>
  )
}
